import { trackDesignSystemAction } from 'analytics';
import { DesignSystem, DesignSystemVersion } from 'dragonmouse-shared/interfaces';
import { useNavigate } from 'react-router';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Icon, { IconType } from 'components/Icon';
import { setChatDesignSystemMode } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import * as routes from 'routes';
import { Loader } from 'components/Widget/Loader';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import * as S from './styles';

interface ActiveDesignSystemSectionProps {
	activeDesignSystem: DesignSystem;
	linkedVersion: DesignSystemVersion;
	latestPublishedVersion: DesignSystemVersion;
	onUpgrade?: () => Promise<void>;
	onClose: () => void;
	updatePendingState?: 'publishing' | 'ready' | null;
}

const ActiveDesignSystemSection = ({
	activeDesignSystem,
	linkedVersion,
	latestPublishedVersion,
	onUpgrade,
	onClose,
	updatePendingState = null,
}: ActiveDesignSystemSectionProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isUpgrading, setIsUpgrading] = useState(false);

	const { isMobile, dsModes, chatDesignSystemMode } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			dsModes: r.db.activeSceneDesignSystemModes,
			chatDesignSystemMode: r.mode.chatDesignSystemMode,
		}),
		shallowEqual
	);

	const dsVersion = `v${linkedVersion.versionNumber}`;

	// Mode resolution
	const hasModes = dsModes && dsModes.length >= 2;
	const defaultModeId = hasModes ? dsModes.find((t) => t.default)?.id ?? dsModes[0]?.id : null;
	const activeModeId = chatDesignSystemMode ?? defaultModeId;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onEdit = () => {
		navigate(routes.designSystem(activeDesignSystem.id, activeDesignSystem.title));
		onClose();
	};

	const onModeSelect = (modeId: string) => {
		trackDesignSystemAction('switch_mode', { modeId });
		dispatch(setChatDesignSystemMode(modeId === defaultModeId ? null : modeId));
	};

	const onUpgradeClick = async () => {
		if (!onUpgrade) return;
		setIsUpgrading(true);
		try {
			await onUpgrade();
			onClose();
		} catch (error) {
			console.error('Failed to upgrade design system version:', error);
		} finally {
			setIsUpgrading(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<S.Base>
			<S.ActiveLabel>Active Design System</S.ActiveLabel>
			<S.Header onClick={isMobile ? undefined : onEdit} $clickable={!isMobile}>
				<S.NameGroup>
					<S.Name>{activeDesignSystem.title}</S.Name>
					{dsVersion && <S.VersionText>{dsVersion}</S.VersionText>}
				</S.NameGroup>
				{!isMobile && (
					<S.HeaderArrow>
						<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} color={theme.gray[400]} />
					</S.HeaderArrow>
				)}
			</S.Header>
			{hasModes && (
				<S.ModeSection>
					{dsModes.map((t) => (
						<S.ModeOption
							key={t.id}
							$active={t.id === activeModeId}
							onClick={() => onModeSelect(t.id)}
						>
							<S.ModeOptionSwatch $color={t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT} />
							{t.label}
						</S.ModeOption>
					))}
				</S.ModeSection>
			)}
			{updatePendingState && (
				<S.VersionSection>
					<S.SectionLabel>Version Management</S.SectionLabel>
					<S.VersionItem>
						<S.VersionDot green />
						<S.VersionInfo>
							<S.VersionLabel>{dsVersion} (Current)</S.VersionLabel>
						</S.VersionInfo>
					</S.VersionItem>
					<S.VersionItem>
						<S.VersionDot blue />
						<S.VersionInfo>
							<S.VersionLabel>
								v{latestPublishedVersion.versionNumber} (Latest)
								{updatePendingState === 'ready' && <S.ReadyBadge>READY</S.ReadyBadge>}
							</S.VersionLabel>
						</S.VersionInfo>
					</S.VersionItem>
					{updatePendingState === 'publishing' ? (
						<S.ProcessingButton disabled>
							<Loader width={12} height={12} />
							Preparing v{latestPublishedVersion.versionNumber} for use
						</S.ProcessingButton>
					) : (
						<S.UpdateButton onClick={onUpgradeClick} disabled={isUpgrading || !onUpgrade}>
							{isUpgrading ? (
								<>
									<Loader width={12} height={12} />
									Updating to v{latestPublishedVersion.versionNumber}...
								</>
							) : (
								`Upgrade to v${latestPublishedVersion.versionNumber}`
							)}
						</S.UpdateButton>
					)}
				</S.VersionSection>
			)}
		</S.Base>
	);
};

export default ActiveDesignSystemSection;
